import { ApiPermission } from "@bvhi/backend-api";
import {
  AccountBoxIcon,
  AlternateEmailIcon,
  BadgeIcon,
  CodeIcon,
  CollectionsIcon,
  EmailIcon,
  GavelIcon,
  GroupIcon,
  HomeIcon,
  KeyIcon,
  LocationCityIcon,
  PasswordIcon,
  PolicyIcon,
  RecentActorsIcon,
  Settings,
  ShieldIcon,
  SvgIconComponent,
} from "@bvhi/ui-icons";
import { LinkProps, RouteIds } from "@tanstack/react-router";
import { Resources } from "i18next";
import { routeTree } from "../routeTree.gen";

export interface BVHIRoute {
  path: LinkProps["to"];
  id: RouteIds<typeof routeTree>;
  shouldAppearInDrawer: boolean;
  shouldAppearInNavigationBar: boolean;
  shouldAppearInFooter: boolean;
  label?: keyof Resources["routes"];
  icon?: SvgIconComponent;
  permissions?: ApiPermission[];
}

export const FooterNavigation: BVHIRoute[] = [
  {
    path: "/docs",
    id: "/_docs/docs/",
    shouldAppearInDrawer: false,
    shouldAppearInFooter: true,
    shouldAppearInNavigationBar: false,
    label: "docs",
  },
  {
    path: "/terms-of-service",
    id: "/_public/terms-of-service",
    shouldAppearInDrawer: false,
    shouldAppearInNavigationBar: false,
    shouldAppearInFooter: true,
    label: "terms-of-service",
    icon: GavelIcon,
  },
  {
    path: "/terms-of-service2026",
    id: "/_public/terms-of-service2026",
    shouldAppearInDrawer: false,
    shouldAppearInNavigationBar: false,
    shouldAppearInFooter: false,
    label: "terms-of-service2026",
    icon: GavelIcon,
  },
  {
    path: "/data-protection",
    id: "/_public/data-protection",
    shouldAppearInDrawer: false,
    shouldAppearInNavigationBar: false,
    shouldAppearInFooter: true,
    label: "data-protection",
    icon: ShieldIcon,
  },
  {
    path: "/imprint",
    id: "/_public/imprint",
    shouldAppearInDrawer: false,
    shouldAppearInFooter: true,
    shouldAppearInNavigationBar: false,
    label: "imprint",
    icon: PolicyIcon,
  },
  {
    path: "/registration-success",
    id: "/_public/registration-success",
    shouldAppearInDrawer: false,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: false,
  },
];

export const AppNavigationConfig: BVHIRoute[] = [
  {
    path: "/app/dashboard",
    id: "/_main/app/dashboard",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: false,
    label: "dashboard",
    icon: HomeIcon,
  },
  {
    path: "/app/users",
    id: "/_main/app/users",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "users",
    icon: GroupIcon,
    permissions: ["user:list"],
  },
  {
    path: "/app/users/create",
    id: "/_main/app/users/create",
    shouldAppearInDrawer: false,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "users.new",
    permissions: ["user:create"],
  },
  {
    path: "/app/users/$userId",
    id: "/_main/app/users/$userId",
    shouldAppearInDrawer: false,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "users.detail",
    permissions: ["user:read"],
  },
  {
    path: "/app/catalogs",
    id: "/_main/app/catalogs",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "catalogs",
    icon: CollectionsIcon,
    permissions: ["catalog:list"],
  },
  {
    path: "/app/catalogs/catalog/$catalogId",
    id: "/_main/app/catalogs/catalog/$catalogId",
    shouldAppearInDrawer: false,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "catalogs.update",
    permissions: ["catalog:read"],
  },
  {
    path: "/app/catalogs/create",
    id: "/_main/app/catalogs/create",
    shouldAppearInDrawer: false,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "catalogs.create",
    permissions: ["catalog:create"],
  },
  {
    path: "/app/catalogs/catalog/$catalogId/create",
    id: "/_main/app/catalogs/catalog/$catalogId/create",
    shouldAppearInDrawer: false,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "catalog.version.create",
    permissions: ["catalog:create"],
  },
  {
    path: "/app/catalogs/catalog/$catalogId/version/$catalogVersionId",
    id: "/_main/app/catalogs/catalog/$catalogId/version/$catalogVersionId",
    shouldAppearInDrawer: false,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "catalog.version.update",
    permissions: ["catalog:read"],
  },
  {
    path: "/app/catalogs/catalog/$catalogId/rules",
    id: "/_main/app/catalogs/catalog/$catalogId/rules",
    shouldAppearInDrawer: false,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "rules.update",
    permissions: ["catalog:read"],
  },
  {
    path: "/app/pos",
    id: "/_main/app/pos",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "pos",
    icon: LocationCityIcon,
    permissions: ["pos:list"],
  },
  {
    path: "/app/pos/create",
    id: "/_main/app/pos/create",
    shouldAppearInDrawer: false,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "pos.new",
    permissions: ["pos:create"],
  },
  {
    path: "/app/pos/$posId",
    id: "/_main/app/pos/$posId",
    shouldAppearInDrawer: false,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "pos.update",
    permissions: ["pos:read"],
  },
  {
    path: "/app/customers",
    id: "/_main/app/customers",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "customers",
    icon: RecentActorsIcon,
    permissions: ["customer-list:read"],
  },
  {
    path: "/app/verification-codes",
    id: "/_main/app/verification-codes",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "verification-codes",
    icon: CodeIcon,
    permissions: ["verification-code:batch:list"],
  },
  {
    path: "/app/verification-codes/create",
    id: "/_main/app/verification-codes/create",
    shouldAppearInDrawer: false,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "verification-codes.create",
    permissions: ["verification-code:batch:create"],
  },
  {
    path: "/app/verification-codes/$verificationCodeId",
    id: "/_main/app/verification-codes/$verificationCodeId",
    shouldAppearInDrawer: false,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "verification-codes.detail",
    permissions: ["verification-code:batch:read"],
  },
  {
    path: "/app/audiologists",
    id: "/_main/app/audiologists",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "audiologists",
    icon: GroupIcon,
    permissions: ["supplier:audiologist:list"],
  },
  {
    path: "/app/audiologists/$audiologistId",
    id: "/_main/app/audiologists/$audiologistId",
    shouldAppearInDrawer: false,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "audiologists.detail",
    permissions: ["supplier:audiologist:update"],
  },
  {
    path: "/app/api-token",
    id: "/_main/app/api-token",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "codes",
    icon: KeyIcon,
    permissions: ["api-token:list"],
  },
  {
    path: "/app/api-token/create",
    id: "/_main/app/api-token/create",
    shouldAppearInDrawer: false,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "codes.create",
    permissions: ["api-token:create"],
  },
  {
    path: "/app/messages",
    id: "/_main/app/messages",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "messages",
    icon: EmailIcon,
    permissions: ["message:list", "message:info"],
  },
  {
    path: "/app/messages/$messageId",
    id: "/_main/app/messages/$messageId",
    shouldAppearInDrawer: false,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "messages.detail",
    permissions: ["message:read"],
  },
  {
    path: "/app/settings",
    id: "/_main/app/settings",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "settings",
    icon: Settings,
    permissions: ["settings:read"],
  },
  {
    path: "/profile/details",
    id: "/_profile/profile/details",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: false,
    label: "profile",
    icon: AccountBoxIcon,
    permissions: ["me:update"],
  },
  ...FooterNavigation,
];

export const DocsNavigationConfig: BVHIRoute[] = [
  {
    path: "/docs",
    id: "/_docs/docs/",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: false,
    label: "docs.index",
  },
  {
    path: "/docs/api/public",
    id: "/_docs/docs/api/public",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: false,
    label: "docs.api.public",
  },
  {
    path: "/docs/api/public-changelog",
    id: "/_docs/docs/api/public-changelog",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: false,
    label: "docs.api.public-changelog",
  },
  {
    path: "/docs/api/mock",
    id: "/_docs/docs/api/mock",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: false,
    label: "docs.api.mock",
  },
  {
    path: "/docs/api/mock-changelog",
    id: "/_docs/docs/api/mock-changelog",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: false,
    label: "docs.api.mockChangelog",
  },
  ...FooterNavigation,
];

export const getConfigForRouteByRoutId = (routeId: string): BVHIRoute | undefined => {
  return AppNavigationConfig.find((value) => value.id === routeId);
};

export const ProfileNavigationConfig: BVHIRoute[] = [
  {
    path: "/profile/details",
    id: "/_profile/profile/details",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "profile.details",
    icon: AccountBoxIcon,
    permissions: ["me:update"],
  },
  {
    path: "/profile/update-password",
    id: "/_profile/profile/update-password",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "profile.update.password",
    icon: PasswordIcon,
    permissions: ["me:update"],
  },
  {
    path: "/profile/update-email",
    id: "/_profile/profile/update-email",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "profile.update.email",
    icon: AlternateEmailIcon,
    permissions: ["me:update"],
  },
  {
    path: "/profile/update-username",
    id: "/_profile/profile/update-username",
    shouldAppearInDrawer: true,
    shouldAppearInFooter: false,
    shouldAppearInNavigationBar: true,
    label: "profile.update.username",
    icon: BadgeIcon,
    permissions: ["me:update"],
  },
  ...FooterNavigation,
];

export const CompleteNavigationConfig: BVHIRoute[] = [...AppNavigationConfig, ...ProfileNavigationConfig, ...DocsNavigationConfig];
